/**
 * `/vehicles` — customer-facing list of the caller's vehicles.
 *
 * Phase 2 Stream B of the PSM → Customer Portal migration. Adapted from
 * the legacy PSM `portal/src/pages/VehiclesPage.tsx`; the Tailwind /
 * shadcn / lucide-react / responsive split has been replaced with the
 * inline-style UI primitives shipped by the Phase 1 foundation so the
 * page renders cleanly without taking a Tailwind upgrade as a dep.
 *
 * Wire-level shape: `PortalVehicleListResponseSchema` in
 * `@customer-portal/shared`. The BFF (`api/src/routes/portal/vehicles.ts`)
 * already allowlists the response — this page treats `status` /
 * `thumbnail_url` as opaque strings.
 *
 * Spec: PORTAL_MIGRATION_FROM_PSM_SSOT.md §4.2 (Phase 2).
 */
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import type { PortalVehicleSummary } from '@customer-portal/shared';
import {
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  EmptyState,
  ErrorState,
  Spinner,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
} from '../components/ui';
import { PortalFeatureDisabledError, apiGet } from '../lib/api';
import { PortalApiError } from '../lib/apiClient';

interface ListEnvelope {
  ok: true;
  data: {
    items: PortalVehicleSummary[];
    total: number;
    limit: number;
    offset: number;
  };
}

type LoadState =
  | { kind: 'idle' }
  | { kind: 'loading' }
  | { kind: 'success'; items: PortalVehicleSummary[]; total: number }
  | { kind: 'feature_disabled' }
  | { kind: 'error'; code: string };

const STATUS_OPTIONS = [
  { value: 'all', label: 'All statuses' },
  { value: 'Preparing', label: 'Preparing' },
  { value: 'Ready for shipment', label: 'Ready for shipment' },
  { value: 'Shipping arranged', label: 'Shipping arranged' },
  { value: 'Loaded', label: 'Loaded' },
  { value: 'In transit', label: 'In transit' },
  { value: 'Arrived at port', label: 'Arrived at port' },
  { value: 'Delivered', label: 'Delivered' },
] as const;

export function VehiclesPage(): JSX.Element {
  const [load, setLoad] = useState<LoadState>({ kind: 'idle' });
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');

  useEffect(() => {
    const ctrl = new AbortController();
    setLoad({ kind: 'loading' });
    void (async () => {
      try {
        const res = await apiGet<ListEnvelope>('/vehicles?limit=100', ctrl.signal);
        const items = res.data?.items ?? [];
        setLoad({ kind: 'success', items, total: res.data?.total ?? items.length });
      } catch (err) {
        if (ctrl.signal.aborted) return;
        if (err instanceof PortalFeatureDisabledError) {
          setLoad({ kind: 'feature_disabled' });
          return;
        }
        if (err instanceof PortalApiError) {
          setLoad({ kind: 'error', code: err.code });
          return;
        }
        setLoad({ kind: 'error', code: 'UNKNOWN' });
      }
    })();
    return () => ctrl.abort();
  }, []);

  const filtered = useMemo(() => {
    if (load.kind !== 'success') return [] as PortalVehicleSummary[];
    const q = search.trim().toLowerCase();
    return load.items.filter((v) => {
      if (statusFilter !== 'all' && v.status !== statusFilter) return false;
      if (q.length === 0) return true;
      return (
        v.chassis_number.toLowerCase().includes(q) ||
        v.maker_name.toLowerCase().includes(q) ||
        v.model_name.toLowerCase().includes(q)
      );
    });
  }, [load, search, statusFilter]);

  return (
    <section data-testid="vehicles-page" style={{ display: 'grid', gap: '1.25rem' }}>
      <header>
        <h1 style={{ margin: 0, fontSize: '1.5rem', fontWeight: 700, color: '#111827' }}>
          Vehicles
        </h1>
        <p style={{ margin: '0.25rem 0 0', fontSize: '0.875rem', color: '#6b7280' }}>
          View and track your purchased vehicles.
        </p>
      </header>

      <Card aria-label="Filters">
        <CardHeader>
          <CardTitle level={2}>Filter</CardTitle>
        </CardHeader>
        <CardBody>
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'end',
              gap: '1rem',
            }}
          >
            <label style={{ flex: '1 1 200px', display: 'grid', gap: '0.25rem' }}>
              <span style={{ fontSize: '0.8125rem', color: '#374151', fontWeight: 600 }}>
                Search
              </span>
              <input
                type="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Chassis, maker, model…"
                style={{
                  padding: '0.5rem 0.75rem',
                  fontSize: '0.875rem',
                  border: '1px solid #d1d5db',
                  borderRadius: '0.375rem',
                  background: '#fff',
                }}
              />
            </label>
            <label style={{ flex: '0 0 200px', display: 'grid', gap: '0.25rem' }}>
              <span style={{ fontSize: '0.8125rem', color: '#374151', fontWeight: 600 }}>
                Status
              </span>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                style={{
                  padding: '0.5rem 0.75rem',
                  fontSize: '0.875rem',
                  border: '1px solid #d1d5db',
                  borderRadius: '0.375rem',
                  background: '#fff',
                }}
              >
                {STATUS_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </label>
          </div>
        </CardBody>
      </Card>

      <ListBody state={load} filtered={filtered} />
    </section>
  );
}

interface ListBodyProps {
  state: LoadState;
  filtered: PortalVehicleSummary[];
}

function ListBody({ state, filtered }: ListBodyProps): JSX.Element {
  if (state.kind === 'idle' || state.kind === 'loading') {
    return (
      <div aria-busy="true" data-testid="vehicles-loading">
        <Spinner block label="Loading vehicles" />
      </div>
    );
  }
  if (state.kind === 'feature_disabled') {
    return (
      <div data-testid="vehicles-feature-disabled">
        <ErrorState
          title="Vehicle list unavailable"
          description="This feature is preparing. Please check back soon."
        />
      </div>
    );
  }
  if (state.kind === 'error') {
    return (
      <div data-testid="vehicles-error">
        <ErrorState
          title="Failed to load vehicles"
          description={`Error code: ${state.code}`}
          onRetry={() => window.location.reload()}
        />
      </div>
    );
  }
  if (state.items.length === 0) {
    return (
      <div data-testid="vehicles-empty">
        <EmptyState
          title="No vehicles yet"
          description="Vehicles you have purchased will appear here as they are processed."
        />
      </div>
    );
  }
  if (filtered.length === 0) {
    return (
      <div data-testid="vehicles-filtered-empty">
        <EmptyState
          title="No vehicles match the current filters"
          description="Adjust the search box or status filter to widen the result set."
        />
      </div>
    );
  }
  return (
    <Card aria-label="Vehicle list">
      <Table caption={`${filtered.length} of ${state.total} vehicles`}>
        <TableHead>
          <TableRow>
            <TableHeaderCell>Chassis #</TableHeaderCell>
            <TableHeaderCell>Vehicle</TableHeaderCell>
            <TableHeaderCell style={{ textAlign: 'right' }}>Mileage</TableHeaderCell>
            <TableHeaderCell>Color</TableHeaderCell>
            <TableHeaderCell>Status</TableHeaderCell>
            <TableHeaderCell style={{ textAlign: 'right' }}>Actions</TableHeaderCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {filtered.map((v) => (
            <TableRow key={v.id} hover>
              <TableCell style={{ fontFamily: 'ui-monospace, SFMono-Regular, monospace' }}>
                {v.chassis_number}
              </TableCell>
              <TableCell>
                <div style={{ fontWeight: 600, color: '#111827' }}>
                  {v.maker_name} {v.model_name}
                </div>
                <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                  {v.model_year != null ? `${v.model_year}` : '—'}
                </div>
              </TableCell>
              <TableCell style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                {v.odometer_km.toLocaleString('en-US')} km
              </TableCell>
              <TableCell>{v.body_color || '—'}</TableCell>
              <TableCell>
                <VehicleStatusPill status={v.status} />
              </TableCell>
              <TableCell style={{ textAlign: 'right' }}>
                <Link
                  to={`/vehicles/${v.id}`}
                  style={{
                    color: '#1f4ed8',
                    fontSize: '0.8125rem',
                    fontWeight: 600,
                    textDecoration: 'none',
                  }}
                  data-testid={`vehicle-link-${v.id}`}
                >
                  View →
                </Link>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Card>
  );
}

function VehicleStatusPill({ status }: { status: string }): JSX.Element {
  const s = status.toLowerCase();
  let bg = '#f3f4f6';
  let color = '#374151';
  if (s.includes('transit')) {
    bg = '#dbeafe';
    color = '#1e40af';
  } else if (s.includes('arrived') || s.includes('delivered')) {
    bg = '#d1fae5';
    color = '#065f46';
  } else if (s.includes('ready') || s.includes('arranged') || s.includes('loaded')) {
    bg = '#e0e7ff';
    color = '#3730a3';
  } else if (s.includes('preparing') || s.includes('inspection')) {
    bg = '#fef3c7';
    color = '#92400e';
  }
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        padding: '0.125rem 0.5rem',
        borderRadius: '9999px',
        fontSize: '0.6875rem',
        fontWeight: 600,
        whiteSpace: 'nowrap',
        background: bg,
        color,
      }}
    >
      {status || '—'}
    </span>
  );
}
